import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Plus, Edit, Trash2, Camera, Calendar, ChevronDown, ChevronRight, Image as ImageIcon, Eye, EyeOff, X } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../../../components/ImageUpload';
import {
  getAlbums,
  createAlbum,
  updateAlbum,
  deleteAlbum,
  getAlbumPhotos,
  getPhotos,
  createPhoto,
  updatePhoto,
  deletePhoto,
  assignPhotoToAlbum,
} from '../../../services/api';

const EMPTY_ALBUM_FORM = {
  title: { sv: '', en: '' },
  description: { sv: '', en: '' },
  event_date: new Date().toISOString().slice(0, 10),
  cover_image_url: '',
  is_published: true,
};

const EMPTY_PHOTO_FORM = {
  title: { sv: '', en: '' },
  description: { sv: '', en: '' },
  image_url: '',
  activity: 'community', // legacy field, defaulted
};

const formatEventDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const PhotoAlbumsManager = () => {
  const [albums, setAlbums] = useState([]);
  const [albumPhotos, setAlbumPhotos] = useState({}); // { [albumId]: Photo[] }
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Album dialog
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [albumForm, setAlbumForm] = useState(EMPTY_ALBUM_FORM);

  // Photo dialog (add or edit photo in specific album)
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [targetAlbumId, setTargetAlbumId] = useState(null);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [photoForm, setPhotoForm] = useState(EMPTY_PHOTO_FORM);

  // Unassigned photos panel
  const [unassignedPhotos, setUnassignedPhotos] = useState([]);
  const [showUnassigned, setShowUnassigned] = useState(false);

  const loadAlbums = useCallback(async () => {
    try {
      const data = await getAlbums(true); // include unpublished for admin
      setAlbums(data || []);
    } catch (err) {
      toast.error('Kunde inte ladda album');
    }
  }, []);

  const loadUnassigned = useCallback(async () => {
    try {
      const all = await getPhotos();
      setUnassignedPhotos((all || []).filter((p) => !p.album_id));
    } catch (err) {
      console.error('Failed to load unassigned photos', err);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadAlbums(), loadUnassigned()]);
      setLoading(false);
    })();
  }, [loadAlbums, loadUnassigned]);

  const loadAlbumPhotos = async (albumId) => {
    try {
      const photos = await getAlbumPhotos(albumId);
      setAlbumPhotos((prev) => ({ ...prev, [albumId]: photos || [] }));
    } catch (err) {
      toast.error('Kunde inte ladda foton');
    }
  };

  const toggleExpand = (albumId) => {
    if (expandedId === albumId) {
      setExpandedId(null);
    } else {
      setExpandedId(albumId);
      if (!albumPhotos[albumId]) loadAlbumPhotos(albumId);
    }
  };

  // -------- Album CRUD --------
  const openCreateAlbum = () => {
    setEditingAlbum(null);
    setAlbumForm(EMPTY_ALBUM_FORM);
    setAlbumDialogOpen(true);
  };

  const openEditAlbum = (album) => {
    setEditingAlbum(album);
    setAlbumForm({
      title: album.title || { sv: '', en: '' },
      description: album.description || { sv: '', en: '' },
      event_date: album.event_date ? new Date(album.event_date).toISOString().slice(0, 10) : '',
      cover_image_url: album.cover_image_url || '',
      is_published: album.is_published !== false,
    });
    setAlbumDialogOpen(true);
  };

  const submitAlbum = async (e) => {
    e.preventDefault();
    if (!albumForm.title.sv?.trim()) {
      toast.error('Titel (svenska) krävs');
      return;
    }
    if (!albumForm.event_date) {
      toast.error('Datum krävs');
      return;
    }
    const payload = {
      ...albumForm,
      event_date: new Date(albumForm.event_date).toISOString(),
    };
    try {
      if (editingAlbum) {
        await updateAlbum(editingAlbum.id, payload);
        toast.success('Album uppdaterat');
      } else {
        await createAlbum(payload);
        toast.success('Album skapat');
      }
      setAlbumDialogOpen(false);
      loadAlbums();
    } catch (err) {
      toast.error('Ett fel uppstod');
    }
  };

  const handleDeleteAlbum = async (album) => {
    if (!window.confirm(`Radera albumet "${album.title?.sv || 'utan titel'}"? Foton behålls och avlänkas.`)) return;
    try {
      await deleteAlbum(album.id);
      toast.success('Album raderat');
      setExpandedId(null);
      loadAlbums();
      loadUnassigned();
    } catch (err) {
      toast.error('Kunde inte radera');
    }
  };

  // -------- Photo actions --------
  const openAddPhoto = (albumId) => {
    setTargetAlbumId(albumId);
    setEditingPhoto(null);
    setPhotoForm(EMPTY_PHOTO_FORM);
    setPhotoDialogOpen(true);
  };

  const openEditPhoto = (albumId, photo) => {
    setTargetAlbumId(albumId);
    setEditingPhoto(photo);
    setPhotoForm({
      title: photo.title || { sv: '', en: '' },
      description: photo.description || { sv: '', en: '' },
      image_url: photo.image_url || '',
      activity: photo.activity || 'community',
    });
    setPhotoDialogOpen(true);
  };

  const submitPhoto = async (e) => {
    e.preventDefault();
    if (!photoForm.image_url) {
      toast.error('Bild krävs');
      return;
    }
    try {
      if (editingPhoto) {
        await updatePhoto(editingPhoto.id, { ...photoForm, album_id: targetAlbumId });
        toast.success('Foto uppdaterat');
      } else {
        await createPhoto({ ...photoForm, album_id: targetAlbumId });
        toast.success('Foto tillagt');
      }
      setPhotoDialogOpen(false);
      setEditingPhoto(null);
      await loadAlbumPhotos(targetAlbumId);
      loadAlbums();
    } catch (err) {
      toast.error(editingPhoto ? 'Kunde inte uppdatera foto' : 'Kunde inte lägga till foto');
    }
  };

  const handleRemovePhoto = async (albumId, photo) => {
    if (!window.confirm(`Ta bort "${photo.title?.sv || 'foto'}" från albumet?`)) return;
    try {
      await deletePhoto(photo.id);
      toast.success('Foto raderat');
      await loadAlbumPhotos(albumId);
      loadAlbums();
    } catch (err) {
      toast.error('Kunde inte radera');
    }
  };

  const handleAssignUnassigned = async (photoId, albumId) => {
    try {
      await assignPhotoToAlbum(photoId, albumId);
      toast.success('Foto tilldelat album');
      loadUnassigned();
      loadAlbums();
      if (expandedId === albumId) loadAlbumPhotos(albumId);
    } catch (err) {
      toast.error('Kunde inte tilldela foto');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Laddar album...</div>;
  }

  return (
    <div data-testid="photo-albums-manager">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Fotoalbum</h2>
          <p className="text-sm text-gray-500">Skapa album per händelse och gruppera foton</p>
        </div>
        <Button onClick={openCreateAlbum} data-testid="new-album-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nytt album
        </Button>
      </div>

      {/* Unassigned photos toggle */}
      {unassignedPhotos.length > 0 && (
        <Card className="mb-4 border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <button
              onClick={() => setShowUnassigned((v) => !v)}
              className="flex items-center justify-between w-full text-left"
              data-testid="toggle-unassigned"
            >
              <span className="text-sm font-medium text-yellow-900">
                {unassignedPhotos.length} foton utan album
              </span>
              {showUnassigned ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {showUnassigned && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {unassignedPhotos.map((p) => (
                  <div key={p.id} className="relative group">
                    <img src={p.image_url} alt="" className="w-full h-24 object-cover rounded" />
                    <div className="mt-1">
                      <select
                        onChange={(e) => e.target.value && handleAssignUnassigned(p.id, e.target.value)}
                        defaultValue=""
                        className="w-full text-xs border rounded px-1 py-0.5"
                        data-testid={`assign-photo-${p.id}`}
                      >
                        <option value="">— Tilldela album —</option>
                        {albums.map((a) => (
                          <option key={a.id} value={a.id}>{a.title?.sv || 'Utan titel'}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Albums list */}
      {albums.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Camera className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Inga album ännu. Skapa ditt första album.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {albums.map((album) => {
            const isOpen = expandedId === album.id;
            const photos = albumPhotos[album.id] || [];
            return (
              <Card key={album.id} data-testid={`album-row-${album.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <button
                      onClick={() => toggleExpand(album.id)}
                      className="flex-shrink-0 w-24 h-24 bg-gray-100 rounded overflow-hidden relative"
                      aria-label="Toggle photos"
                    >
                      {album.cover_image_url ? (
                        <img src={album.cover_image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <ImageIcon className="w-8 h-8" />
                        </div>
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {album.title?.sv || 'Utan titel'}
                        </h3>
                        {album.is_published ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded" title="Publicerat">
                            <Eye className="w-3 h-3" /> Publicerat
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded" title="Dolt">
                            <EyeOff className="w-3 h-3" /> Dolt
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatEventDate(album.event_date)}</span>
                        <span className="inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" />{album.photo_count || 0} foton</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => toggleExpand(album.id)} data-testid={`toggle-album-${album.id}`}>
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        {isOpen ? 'Dölj foton' : 'Visa foton'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEditAlbum(album)} data-testid={`edit-album-${album.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteAlbum(album)} data-testid={`delete-album-${album.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-4 border-t pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <p className="text-sm font-medium text-gray-700">Foton i detta album</p>
                        <Button size="sm" onClick={() => openAddPhoto(album.id)} data-testid={`add-photo-${album.id}`}>
                          <Plus className="w-4 h-4 mr-1" /> Lägg till foto
                        </Button>
                      </div>
                      {photos.length === 0 ? (
                        <p className="text-sm text-gray-400 py-4 text-center">Inga foton ännu</p>
                      ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                          {photos.map((photo) => (
                            <button
                              type="button"
                              key={photo.id}
                              onClick={() => openEditPhoto(album.id, photo)}
                              className="relative group aspect-square rounded overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                              data-testid={`album-photo-${photo.id}`}
                              title="Klicka för att redigera"
                            >
                              <img src={photo.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); handleRemovePhoto(album.id, photo); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleRemovePhoto(album.id, photo); } }}
                                className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                aria-label="Delete photo"
                                data-testid={`delete-photo-${photo.id}`}
                              >
                                <X className="w-3 h-3" />
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Album Create/Edit Dialog */}
      <Dialog open={albumDialogOpen} onOpenChange={setAlbumDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAlbum ? 'Redigera album' : 'Nytt album'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAlbum} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titel (Svenska) *</label>
                <Input
                  required
                  value={albumForm.title.sv}
                  onChange={(e) => setAlbumForm({ ...albumForm, title: { ...albumForm.title, sv: e.target.value } })}
                  data-testid="album-title-sv"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Titel (English)</label>
                <Input
                  value={albumForm.title.en}
                  onChange={(e) => setAlbumForm({ ...albumForm, title: { ...albumForm.title, en: e.target.value } })}
                  data-testid="album-title-en"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Beskrivning (Svenska)</label>
                <Input
                  value={albumForm.description.sv}
                  onChange={(e) => setAlbumForm({ ...albumForm, description: { ...albumForm.description, sv: e.target.value } })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Beskrivning (English)</label>
                <Input
                  value={albumForm.description.en}
                  onChange={(e) => setAlbumForm({ ...albumForm, description: { ...albumForm.description, en: e.target.value } })}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Datum för händelsen *</label>
                <Input
                  type="date"
                  required
                  value={albumForm.event_date}
                  onChange={(e) => setAlbumForm({ ...albumForm, event_date: e.target.value })}
                  data-testid="album-date"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="album-published"
                  checked={albumForm.is_published}
                  onChange={(e) => setAlbumForm({ ...albumForm, is_published: e.target.checked })}
                  data-testid="album-published"
                />
                <label htmlFor="album-published" className="text-sm">Publicerat (synligt på webbplatsen)</label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Omslagsbild (valfritt – annars används första fotot)</label>
              <ImageUpload
                value={albumForm.cover_image_url}
                onChange={(url) => setAlbumForm({ ...albumForm, cover_image_url: url })}
                aspectRatio="video"
                placeholder="Ladda upp omslagsbild"
                data-testid="album-cover-upload"
              />
              <Input
                type="url"
                value={albumForm.cover_image_url}
                onChange={(e) => setAlbumForm({ ...albumForm, cover_image_url: e.target.value })}
                placeholder="https://..."
                className="mt-2"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAlbumDialogOpen(false)}>Avbryt</Button>
              <Button type="submit" data-testid="save-album-btn">{editingAlbum ? 'Spara' : 'Skapa'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Photo Add/Edit Dialog */}
      <Dialog open={photoDialogOpen} onOpenChange={(open) => { setPhotoDialogOpen(open); if (!open) setEditingPhoto(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPhoto ? 'Redigera foto' : 'Lägg till foto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPhoto} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Bild *</label>
              <ImageUpload
                value={photoForm.image_url}
                onChange={(url) => setPhotoForm({ ...photoForm, image_url: url })}
                aspectRatio="square"
                placeholder="Ladda upp foto"
                data-testid="photo-image-upload"
              />
              <Input
                type="url"
                value={photoForm.image_url}
                onChange={(e) => setPhotoForm({ ...photoForm, image_url: e.target.value })}
                placeholder="https://..."
                className="mt-2"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titel (Svenska)</label>
                <Input
                  value={photoForm.title.sv}
                  onChange={(e) => setPhotoForm({ ...photoForm, title: { ...photoForm.title, sv: e.target.value } })}
                  data-testid="photo-title-sv"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Titel (English)</label>
                <Input
                  value={photoForm.title.en}
                  onChange={(e) => setPhotoForm({ ...photoForm, title: { ...photoForm.title, en: e.target.value } })}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Beskrivning (valfritt)</label>
              <Input
                value={photoForm.description.sv}
                onChange={(e) => setPhotoForm({ ...photoForm, description: { ...photoForm.description, sv: e.target.value } })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setPhotoDialogOpen(false); setEditingPhoto(null); }}>Avbryt</Button>
              <Button type="submit" data-testid="save-photo-btn">{editingPhoto ? 'Spara' : 'Lägg till'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoAlbumsManager;
